import { Injectable, OnDestroy } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { OccEndpointsService } from '@spartacus/core'
import { MbConstants } from '../../../shared/mb-constants'
import { bufferTime, catchError, distinct, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators'
import { Observable, of, Subject, Subscription } from 'rxjs'
import { CategoriesResponse } from '../../components/types'
import { MbEventListenerService } from '../mb-event-listener/event-listener.service'
import { ProductResolvedEvent } from '../../components/shared/events/product-resolved-event'

@Injectable({
  providedIn: 'root'
})
export class CmsProductService implements OnDestroy {
  private static productQuery = 'productSearchMinimal'
  private static productCategoriesQuery = 'navigationCategories'

  private bufferEntries = new Subject<string>()
  private batchSubscription?: Subscription

  constructor (private http: HttpClient,
               private occEndpoints: OccEndpointsService,
               private eventService: MbEventListenerService) {
  }

  private initiateBatchRequest (): void {
    if (this.batchSubscription) {
      return
    }

    this.batchSubscription = this.bufferEntries.pipe(
      distinct(),
      bufferTime(100, undefined, 200),
      filter(entries => entries && entries.length > 0),
      switchMap((productIds: string[]) => this.getProductsByIds(productIds)),
      tap(results => this.handleProductResults(results)),
      tap(() => {
        if (this.batchSubscription) {
          this.batchSubscription.unsubscribe()
          this.batchSubscription = undefined
        }
      })
    ).subscribe()
  }

  handleProductResults (results: any): void {
    results?.products?.flatMap((product: any) => product?.variantOptions ?? [])
      .forEach((variant: any) => this.eventService.update(new ProductResolvedEvent(variant.code, variant.url)))
  }

  batchProductRequests (code: string): void {
    if (!this.batchSubscription) {
      this.initiateBatchRequest()
    }
    this.bufferEntries.next(code)
  }

  getCategoriesWithAvailableProducts (): Observable<string[]> {
    return this.http.get<CategoriesResponse>(this.occEndpoints.buildUrl(CmsProductService.productCategoriesQuery)).pipe(
      shareReplay(1),
      take(1),
      map((res: CategoriesResponse) => res?.productCategories ?? []),
      map(categories => categories.map(category => category.code)),
      catchError(e => {
        console.error(e)
        return of([])
      }))
  }

  getProductsByCategoryId (categoryId: string): Observable<any> {
    const query = `:${MbConstants.RELEVANCE}:${MbConstants.ALL_CATEGORIES}:${categoryId}`
    return this.http.get(this.occEndpoints.buildUrl(CmsProductService.productQuery), { params: { query } }).pipe(
      shareReplay(1),
      filter((res: any) => (res?.currentQuery?.query?.value ?? '').includes(query.toLowerCase())),
      take(1),
      catchError(e => {
        console.error(e)
        return of(undefined)
      }))
  }

  getProductsByIds (productIds: string[]): Observable<any> {
    const query = `:${MbConstants.RELEVANCE}${productIds.map(productId => `:${MbConstants.CODE_STRING}:${productId}`).join('')}`
    return this.http.get(this.occEndpoints.buildUrl(CmsProductService.productQuery), {
      params: {
        query,
        pageSize: productIds.length
      }
    }).pipe(
      catchError(e => {
        console.error(e)
        return of(undefined)
      }))
  }

  hasCategoryAvailableProducts (categoryId: string): Observable<boolean> {
    return this.getProductsByCategoryId(categoryId).pipe(
      map(res => res?.products?.length > 0))
  }

  ngOnDestroy (): void {
    this.batchSubscription?.unsubscribe()
  }
}
